// Role-Based Access Control — client mirror of server/middleware/roles.js
// Roles (canonical, lowercase): owner | manager | chef | waiter | cashier

export const ACCESS = {
  dashboard:    ['owner', 'manager'],
  pos:          ['owner', 'manager', 'waiter', 'cashier'],
  kitchen:      ['owner', 'manager', 'chef'],
  orders:       ['owner', 'manager', 'waiter', 'cashier'],
  menu:         ['owner', 'manager', 'chef', 'waiter', 'cashier'],
  inventory:    ['owner', 'manager', 'chef'],
  reservations: ['owner', 'manager', 'waiter'],
  bills:        ['owner', 'manager', 'cashier'],
  coupons:      ['owner', 'manager'],
  staff:        ['owner', 'manager'],
  members:      ['owner', 'manager', 'cashier'],
  accounts:     ['owner'],
  reports:      ['owner', 'manager'],
  settings:     ['owner', 'manager'],
  subscription: ['owner'],
  support:      ['owner', 'manager'],
  branches:     ['owner'],
  expenses:     ['owner', 'manager'],
  attendance:   ['owner', 'manager'],
  delivery:     ['owner', 'manager', 'waiter'],
  reviews:      ['owner', 'manager'],
  production:   ['owner', 'manager'],
  recipes:      ['owner', 'manager', 'chef'],
  purchases:    ['owner', 'manager'],
  tables:       ['owner', 'manager'],
  permissions:  ['owner'],
  accounting:   ['owner', 'manager'],
  payroll:      ['owner', 'manager'],
  fixedassets:  ['owner', 'manager'],
  gst:          ['owner', 'manager'],
  integrations: ['owner', 'manager'],
  qr:           ['owner', 'manager'],
  mydeliveries: ['owner', 'manager', 'delivery'],
};

export const can = (role, area) =>
  (ACCESS[area] || []).includes((role || '').toLowerCase());

// Granular per-action permission check (mirrors server/middleware/roles.js).
export const ACTIONS = ['view', 'add', 'edit', 'delete', 'download'];
function normPerms(perms) {
  if (!perms) return null;
  if (Array.isArray(perms)) { const m = {}; perms.forEach(a => { m[a] = ACTIONS.slice(); }); return m; }
  if (typeof perms === 'object') return perms;
  return null;
}
// userCan(user, 'menu', 'edit') — user = { role, perms, features }
export function userCan(user, area, action = 'view') {
  if (!user) return false;
  if (area === 'my_attendance') return true;   // personal attendance — available to every staff member
  const role = (user.role || '').toLowerCase();
  const featureArea = area === 'mydeliveries' ? 'delivery' : area;
  const featureOk = !(user.features && Array.isArray(user.features)) ||
    user.features.includes(featureArea) || CORE_OWNER_AREAS.includes(featureArea);
  if (role === 'owner') return CORE_OWNER_AREAS.includes(area) ? true : featureOk;
  if (!featureOk) return false;
  const map = normPerms(user.perms);
  if (map) {
    const acts = map[area];
    if (!Array.isArray(acts) || !acts.length) return false;
    if (action === 'view') return true;
    return acts.includes(action);
  }
  return (ACCESS[area] || []).includes(role);
}
// Convenience: read the logged-in user from storage.
export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch (_) { return null; }
}

// All known feature areas (used by the SaaS add-on picker).
export const ALL_AREAS = Object.keys(ACCESS);

// Capability flags — paid add-ons that are NOT navigable modules. The super admin
// toggles these per package; they live in the same `features` array as areas.
export const CAPABILITY_LABELS = {
  menu_images: 'Image / File Uploads (Cloudinary)',
  data_reset: 'Self-service Data Reset (owner can delete branch data)',
};
export const ALL_CAPABILITIES = Object.keys(CAPABILITY_LABELS);

// Is a plan capability/area enabled for this user? When the org has no plan
// (features is null) nothing is restricted — matches the rest of the system.
export function hasFeature(user, key) {
  const f = user && user.features;
  if (!Array.isArray(f)) return true;
  return f.includes(key) || CORE_OWNER_AREAS.includes(key);
}

// Control-plane areas an owner always keeps, regardless of plan features.
// Mirror of server/middleware/roles.js CORE_OWNER_AREAS.
export const CORE_OWNER_AREAS = ['dashboard', 'branches', 'accounts', 'staff', 'settings', 'permissions', 'subscription', 'support'];

// Where each role lands right after login.
export const defaultRouteForRole = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'superadmin': return '/super';
    case 'chef':    return '/admin/kitchen';
    case 'waiter':  return '/admin/pos';
    case 'cashier': return '/admin/pos';
    case 'delivery': return '/delivery';
    default:        return '/admin'; // owner / manager
  }
};

// Landing page per area, in preference order (used when the role default is not
// accessible, e.g. custom perms without 'dashboard').
const AREA_LANDING = [
  ['dashboard', '/admin'], ['pos', '/admin/pos'], ['kitchen', '/admin/kitchen'],
  ['orders', '/admin/orders'], ['mydeliveries', '/delivery'], ['menu', '/admin/menu'],
  ['tables', '/admin/tables'], ['bills', '/admin/bills'], ['reservations', '/admin/reservations'],
  ['inventory', '/admin/inventory'], ['reports', '/admin/reports'], ['settings', '/admin/settings'],
];

// A route this user can actually open — never one that would bounce them again
// (which caused an infinite redirect / blank page). null = no accessible page.
export function landingRouteFor(user) {
  const role = (user?.role || '').toLowerCase();
  if (role === 'superadmin') return '/super';
  const preferred = defaultRouteForRole(role);
  const hit = AREA_LANDING.find(([area, path]) => path === preferred && userCan(user, area));
  if (hit) return hit[1];
  const any = AREA_LANDING.find(([area]) => userCan(user, area));
  if (any) return any[1];
  return user?.staff_id ? '/admin/my-attendance' : null;
}

// Human-friendly labels for each access area (used by the User Rights page).
export const AREA_LABELS = {
  dashboard: 'Dashboard', pos: 'POS / Tables', kitchen: 'Kitchen Display', tables: 'Tables',
  orders: 'All Orders', delivery: 'Delivery', menu: 'Menu & Item Import', inventory: 'Inventory & Stock Import',
  production: 'Production', recipes: 'Recipe & Import', purchases: 'Purchases', reservations: 'Reservations', bills: 'Bills History',
  coupons: 'Coupons', reviews: 'Reviews', staff: 'Staff', attendance: 'Attendance', members: 'Members',
  accounts: 'Accounts', reports: 'Reports & Analytics', expenses: 'Expenses', settings: 'Settings',
  subscription: 'Subscription & Billing',
  website: 'Customer Website', permissions: 'User Rights',
  accounting: 'Accounting (Tally)', payroll: 'Payroll', fixedassets: 'Fixed Assets',
  gst: 'GST Returns', integrations: 'Integrations (Aggregators)', qr: 'QR Ordering',
  mydeliveries: 'My Deliveries',
};
